import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getProgress, saveProgress } from '../services/api';

import WizardLayout from '../components/wizard/WizardLayout';
import { computeStepStatuses } from '../components/wizard/stepStatus';
import {
  NON_SKIPPABLE, STEPS, hiddenSteps, migrateProgress, nextStep, prevStep, stepIndex, visibleStep,
} from '../components/wizard/steps';
import StepWelcome from '../components/wizard/StepWelcome';
import StepImport from '../components/wizard/StepImport';
import { importIncome } from '../components/wizard/importMerge';
import StepIncome from '../components/wizard/StepIncome';
import StepHousing from '../components/wizard/StepHousing';
import StepBillsUtilities from '../components/wizard/StepBillsUtilities';
import StepAutoTransport from '../components/wizard/StepAutoTransport';
import StepMedical from '../components/wizard/StepMedical';
import StepInsurance from '../components/wizard/StepInsurance';
import StepEntertainmentTravel from '../components/wizard/StepEntertainmentTravel';
import StepPersonalFamilyCare from '../components/wizard/StepPersonalFamilyCare';
import StepCharity from '../components/wizard/StepCharity';
import StepLegalProfessional from '../components/wizard/StepLegalProfessional';
import StepKidsEducation from '../components/wizard/StepKidsEducation';
import StepSavings from '../components/wizard/StepSavings';
import StepPets from '../components/wizard/StepPets';
import StepDebts from '../components/wizard/StepDebts';
import StepReview from '../components/wizard/StepReview';

/**
 * WizardPage — main controller component.
 *
 * Loads saved progress from the API on mount, renders the current step,
 * and auto-saves progress when the user advances or goes back.
 */
export default function WizardPage() {
  const { client, logout } = useAuth();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [clientType, setClientType] = useState('hff');
  // Document types the Import step accepts (from the server); [] hides the step.
  const [importTypes, setImportTypes] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Ref to avoid stale closures + double-load in StrictMode
  const loadedRef = useRef(false);

  // -----------------------------------------------------------------------
  // Load saved progress on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    async function load() {
      try {
        const data = await getProgress();
        const migrated = migrateProgress(data.current_step, data.form_data);
        setImportTypes(data.import_types ?? undefined);
        setCurrentStep(visibleStep(migrated.step, hiddenSteps(data.import_types)));
        setFormData(migrated.formData);
        setClientType(data.client_type || 'hff');
      } catch (err) {
        console.error('Failed to load progress:', err);
        setError('Unable to load your saved progress. Please reload.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // -----------------------------------------------------------------------
  // Persist progress to backend
  // -----------------------------------------------------------------------
  const persist = useCallback(
    async (step, data, completed = false) => {
      setSaving(true);
      try {
        await saveProgress(step, data, completed);
        setError(null);
      } catch (err) {
        console.error('Save failed:', err);
        setError('Could not save — please check your connection.');
      } finally {
        setSaving(false);
      }
    },
    []
  );

  // -----------------------------------------------------------------------
  // Navigation (hidden steps — Import with nothing to import — are passed over)
  // -----------------------------------------------------------------------
  const hidden = hiddenSteps(importTypes);

  function goNext() {
    const target = nextStep(currentStep, hidden);
    const isLast = currentStep === STEPS.length - 1;

    if (isLast) {
      // Submit
      persist(currentStep, formData, true).then(() =>
        navigate('/complete', { replace: true })
      );
      return;
    }

    setCurrentStep(target);
    persist(target, formData);
    // Scroll content back to top
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function goSkip() {
    const stepKey = STEPS[currentStep]?.key;
    if (!stepKey || NON_SKIPPABLE.has(stepKey)) return;
    const updated = { ...formData, [stepKey]: { ...(formData[stepKey] || {}), _skipped: true } };
    setFormData(updated);
    const target = nextStep(currentStep, hidden);
    setCurrentStep(target);
    persist(target, updated);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function goBack() {
    if (currentStep <= 0) return;
    const target = prevStep(currentStep, hidden);
    setCurrentStep(target);
    persist(target, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  function jumpTo(idx) {
    if (idx === currentStep) return;
    setCurrentStep(idx);
    persist(idx, formData);
    document.querySelector('.cashflow-step-content')?.scrollTo(0, 0);
  }

  // -----------------------------------------------------------------------
  // Section data helpers
  // -----------------------------------------------------------------------
  function sectionData(key) {
    return formData[key] || {};
  }

  function updateSection(key) {
    return (updated) => {
      setFormData((prev) => ({ ...prev, [key]: updated }));
    };
  }

  // Imports fill fields across many sections, so save straight away rather
  // than waiting for Next — a reload shouldn't lose an uploaded file's totals.
  function handleImportChange(updated) {
    setFormData(updated);
    persist(currentStep, updated);
  }

  // -----------------------------------------------------------------------
  // Step renderer
  // -----------------------------------------------------------------------
  function renderStep() {
    const stepKey = STEPS[currentStep]?.key;
    switch (stepKey) {
      case 'welcome':              return <StepWelcome clientName={client?.name} />;
      case 'import':               return <StepImport formData={formData} onImportChange={handleImportChange} importTypes={importTypes} />;
      case 'income':               return <StepIncome data={sectionData('income')} onChange={updateSection('income')} importIncome={importIncome(formData.import?.files || [])} />;
      case 'housing':              return <StepHousing data={sectionData('housing')} onChange={updateSection('housing')} onGoToIncome={() => jumpTo(stepIndex('income'))} />;
      case 'bills_utilities':      return <StepBillsUtilities data={sectionData('bills_utilities')} onChange={updateSection('bills_utilities')} />;
      case 'auto_transport':       return <StepAutoTransport data={sectionData('auto_transport')} onChange={updateSection('auto_transport')} />;
      case 'medical':              return <StepMedical data={sectionData('medical')} onChange={updateSection('medical')} />;
      case 'insurance':            return <StepInsurance data={sectionData('insurance')} onChange={updateSection('insurance')} />;
      case 'entertainment_travel': return <StepEntertainmentTravel data={sectionData('entertainment_travel')} onChange={updateSection('entertainment_travel')} />;
      case 'personal_family_care': return <StepPersonalFamilyCare data={sectionData('personal_family_care')} onChange={updateSection('personal_family_care')} />;
      case 'charity':              return <StepCharity data={sectionData('charity')} onChange={updateSection('charity')} />;
      case 'legal_professional':   return <StepLegalProfessional data={sectionData('legal_professional')} onChange={updateSection('legal_professional')} />;
      case 'kids_education':       return <StepKidsEducation data={sectionData('kids_education')} onChange={updateSection('kids_education')} />;
      case 'savings':              return <StepSavings data={sectionData('savings')} onChange={updateSection('savings')} />;
      case 'pets':                 return <StepPets data={sectionData('pets')} onChange={updateSection('pets')} />;
      case 'debts':                return <StepDebts data={sectionData('debts')} onChange={updateSection('debts')} />;
      case 'review':               return <StepReview formData={formData} />;
      default:                     return <p>Unknown step</p>;
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  // Apply the Rebellionaire theme for any client flagged as Rebellionaire.
  const isRebelClient = clientType === 'rebellionaire';
  const themeClass = isRebelClient ? 'theme-rebellionaire' : '';

  if (loading) {
    return (
      <div className={`d-flex justify-content-center align-items-center vh-100 ${themeClass}`}>
        <div className="text-center">
          <div className="spinner-border text-danger mb-3" role="status" />
          <p className="text-muted">Loading your progress…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={themeClass}>
      {error && (
        <div
          className="alert alert-warning position-fixed bottom-0 start-50 translate-middle-x mb-4 shadow-sm"
          style={{ zIndex: 9999, maxWidth: 500 }}
        >
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <WizardLayout
        currentStep={currentStep}
        steps={STEPS}
        hidden={hidden}
        onBack={goBack}
        onNext={goNext}
        onSkip={goSkip}
        showSkip={(() => {
          const key = STEPS[currentStep]?.key;
          if (!key || NON_SKIPPABLE.has(key)) return false;
          const statuses = computeStepStatuses(formData, STEPS, currentStep);
          return statuses[currentStep] === 'empty';
        })()}
        isFirst={currentStep === 0}
        isLast={currentStep === STEPS.length - 1}
        saving={saving}
        onStepClick={jumpTo}
        stepStatuses={computeStepStatuses(formData, STEPS, currentStep)}
        clientName={client?.name || ''}
        onLogout={logout}
      >
        {renderStep()}
      </WizardLayout>
    </div>
  );
}
